import React, { useState } from "react";
import { User, Bot } from "lucide-react";
import { avatarHue, initialsOf } from "../utils/avatarPalette";

/**
 * O disco de quem fala. Tres variantes, a mesma caixa.
 *
 * Contacto: matiz derivado do nome ou do id (utils/avatarPalette.js), com as
 * iniciais quando ha nome e um glifo de pessoa quando nao ha. Nao se desenha um
 * "A" de "Anonimo": uma inicial promete um nome.
 *
 * Agente: o avatar VERDADEIRO do widget. A config do chat traz copy.botAvatarUrl
 * e copy.botName, ou seja a cara e o nome que o cliente viu do outro lado. Um
 * icone generico escolhido por nos seria uma segunda identidade para o mesmo
 * agente. Quando a imagem falha ou nao existe, cai no glifo sobre a cor do chat.
 *
 * Operador: a foto que a ponte de handoff guardou do Chatwoot, e sem ela o disco
 * do contacto com as iniciais do operador (OperatorAvatar).
 */

// Tamanhos em uso, e o glifo e as iniciais que cabem em cada um. Uma tabela e
// nao aritmetica: sao dois sitios concretos, 32 na linha da lista e no
// cabecalho da transcricao, 20 na byline de cada turno, e cada um foi escolhido
// contra o texto que fica ao lado.
const SIZES = {
  20: { glyph: 12, text: "text-[9px]" },
  32: { glyph: 18, text: "text-xs" },
};

/**
 * O disco e decorativo: em todos os sitios onde aparece, o nome esta ao lado
 * em texto, portanto fica escondido dos leitores de ecra.
 *
 * @param {{
 *   seed?: string|null,      semente do matiz: nome quando existe, senao o id
 *   name?: string|null,      nome a mostrar em iniciais; sem ele desenha o glifo
 *   size?: 20|32,
 *   className?: string,
 * }} props
 */
const ContactAvatar = ({ seed, name, size = 32, className = "" }) => {
  const { glyph, text } = SIZES[size];
  const initials = initialsOf(name);

  return (
    <span
      aria-hidden="true"
      className={`contact-avatar ${text} ${className}`}
      style={{ "--avatar-h": avatarHue(seed || name || ""), width: size, height: size }}
    >
      {initials || <User size={glyph} strokeWidth={2} aria-hidden="true" />}
    </span>
  );
};

/**
 * O disco do agente, na byline de cada turno. `avatarUrl` vem da config do
 * widget do chat, a mesma que ja pinta os baloes e os cartoes. Decorativo,
 * como o do contacto: o nome do agente esta ao lado em texto.
 *
 * @param {{ avatarUrl?: string|null, size?: 20|32, className?: string }} props
 */
export const AgentAvatar = ({ avatarUrl, size = 20, className = "" }) => {
  // Uma imagem que nao carrega deixaria o icone partido do browser no meio da
  // transcricao, e cai-se no glifo sobre a cor do chat. O estado comeca a false
  // e so passa a true depois de uma imagem com URL falhar; o URL vem da config
  // do chat em cache, que muda no maximo uma vez por montagem (undefined
  // enquanto le, depois o valor), portanto nao ha caminho por onde um `broken`
  // velho sobreviva a um URL novo.
  const [broken, setBroken] = useState(false);
  const { glyph } = SIZES[size];

  if (avatarUrl && !broken) {
    return (
      <img
        src={avatarUrl}
        alt=""
        aria-hidden="true"
        width={size}
        height={size}
        loading="lazy"
        // O URL e do CDN do tenant, nao nosso. Sem isto o pedido leva o
        // endereco do backoffice no cabecalho Referer.
        referrerPolicy="no-referrer"
        onError={() => setBroken(true)}
        className={`rounded-full shrink-0 object-cover ${className}`}
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      // O rebordo do escuro: o disco tem o mesmo problema de contorno que o
      // balao, ver .transcript-avatar-agent em index.css.
      className={`transcript-avatar-agent inline-flex items-center justify-center rounded-full shrink-0 ${className}`}
      style={{
        width: size,
        height: size,
        backgroundColor: "var(--transcript-agent-bg)",
        color: "var(--transcript-agent-fg)",
      }}
    >
      <Bot size={glyph} strokeWidth={2} aria-hidden="true" />
    </span>
  );
};

/**
 * O disco do operador, na byline das mensagens do operador. A foto que a ponte
 * guardou do Chatwoot quando ha uma; senao as iniciais sobre um matiz derivado
 * do nome, que e o que o widget mostra ao cliente quando o Chatwoot nao traz foto;
 * sem nome, o glifo de pessoa. Decorativo, como os outros dois: o nome esta ao
 * lado em texto.
 *
 * @param {{ avatarUrl?: string|null, name?: string|null, size?: 20|32 }} props
 */
export const OperatorAvatar = ({ avatarUrl, name, size = 20 }) => {
  // Uma foto que nao carrega cai nas iniciais, pela mesma razao do AgentAvatar,
  // e enquanto carrega as iniciais ficam por baixo dela: o Chatwoot pode demorar,
  // ou nem responder fora da rede dele, e sem isto a byline mostrava um buraco
  // de 20px ao lado do nome. O URL e o do `sender` da primeira mensagem do turno,
  // que nao muda, portanto um `loaded` ou um `broken` nunca sobrevivem a um URL
  // novo.
  const [loaded, setLoaded] = useState(false);
  const [broken, setBroken] = useState(false);
  const initials = <ContactAvatar seed={name} name={name} size={size} />;

  if (!avatarUrl || broken) return initials;

  return (
    <span aria-hidden="true" className="relative inline-flex shrink-0" style={{ width: size, height: size }}>
      {initials}
      <img
        src={avatarUrl}
        alt=""
        width={size}
        height={size}
        loading="lazy"
        // O URL e do Chatwoot do tenant, nao nosso.
        referrerPolicy="no-referrer"
        onLoad={() => setLoaded(true)}
        onError={() => setBroken(true)}
        className={`absolute inset-0 rounded-full object-cover ${loaded ? "" : "opacity-0"}`}
        style={{ width: size, height: size }}
      />
    </span>
  );
};

export default ContactAvatar;
